/**
 * The source of the spacing and type scale.
 *
 * The Ant Design tokens (theme.ts) and the CSS variables (buildScaleCss) both read it,
 * so components and custom CSS agree. See docs/ARCHITECTURE.md § Spacing and Type Scale.
 */

/** From this width the sidebar is in the page and controls use the pointer sizes (Ant Design `xl`). */
export const POINTER_TIER_MIN_WIDTH = 1200;
/** Below this width the page gutter gets smaller (Ant Design `md`). */
const TABLET_MIN_WIDTH = 768;

export type ScaleTier = {
  headerHeight: number;
  cardPadding: number;
  cardHeaderHeight: number;
  sectionGap: number;
  controlHeight: number;
  controlHeightSM: number;
  controlHeightLG: number;
  inputFontSize: number;
  tableFontSize: number;
  detailCellPaddingBlock: number;
  pageTitleFontSize: number;
};

const POINTER: ScaleTier = {
  headerHeight: 48,
  cardPadding: 16,
  cardHeaderHeight: 44,
  sectionGap: 16,
  controlHeight: 32,
  controlHeightSM: 24,
  controlHeightLG: 40,
  inputFontSize: 14,
  tableFontSize: 13,
  detailCellPaddingBlock: 8,
  pageTitleFontSize: 20,
};

const TOUCH: ScaleTier = {
  headerHeight: 56,
  cardPadding: 12,
  // Room for a 40px button in the card header.
  cardHeaderHeight: 52,
  sectionGap: 12,
  controlHeight: 40,
  controlHeightSM: 32,
  controlHeightLG: 44,
  // iOS zooms the page when a focused field has text below 16px.
  inputFontSize: 16,
  tableFontSize: 14,
  detailCellPaddingBlock: 10,
  pageTitleFontSize: 18,
};

export const SCALE = {
  space: [4, 8, 12, 16, 24, 32],
  fontSizeMin: 12,
  // Form labels and table headers.
  fontSizeLabel: 12,
  fontSizeBody: 14,
  fontSizeCardTitle: 16,
  pageGutter: 16,
  pageGutterMobile: 12,
  formItemGap: 16,
  // With the 24px (pointer) or 32px (touch) row buttons, a row is 41px or 49px high.
  tableCellPaddingBlock: 8,
  tableCellPaddingInline: 8,
  siderWidth: 232,
  drawerWidth: 260,
  contentMaxForm: 960,
  contentMaxDetail: 1280,
  pointer: POINTER,
  touch: TOUCH,
} as const;

const px = (value: number): string => `${String(value)}px`;

const tierVariables = (tier: ScaleTier): Array<string> => [
  `--header-h:${px(tier.headerHeight)}`,
  `--card-pad:${px(tier.cardPadding)}`,
  `--section-gap:${px(tier.sectionGap)}`,
  `--control-h:${px(tier.controlHeight)}`,
  `--font-size-input:${px(tier.inputFontSize)}`,
  `--font-size-table:${px(tier.tableFontSize)}`,
  `--font-size-page-title:${px(tier.pageTitleFontSize)}`,
];

const below = (minWidth: number): string => `@media (max-width:${px(minWidth - 0.02)})`;

/** The scale as CSS variables on `:root`, with the touch and mobile values in media queries. */
export const buildScaleCss = (): string => {
  const shared = [
    ...SCALE.space.map((value, index) => `--space-${String(index + 1)}:${px(value)}`),
    `--font-size-min:${px(SCALE.fontSizeMin)}`,
    `--font-size-label:${px(SCALE.fontSizeLabel)}`,
    `--font-size-body:${px(SCALE.fontSizeBody)}`,
    `--font-size-card-title:${px(SCALE.fontSizeCardTitle)}`,
    `--page-gutter:${px(SCALE.pageGutter)}`,
    `--form-item-gap:${px(SCALE.formItemGap)}`,
    `--sider-w:${px(SCALE.siderWidth)}`,
    `--content-max-form:${px(SCALE.contentMaxForm)}`,
    `--content-max-detail:${px(SCALE.contentMaxDetail)}`,
  ];

  return [
    `:root{${[...shared, ...tierVariables(SCALE.pointer)].join(';')}}`,
    `${below(POINTER_TIER_MIN_WIDTH)}{:root{${tierVariables(SCALE.touch).join(';')}}}`,
    `${below(TABLET_MIN_WIDTH)}{:root{--page-gutter:${px(SCALE.pageGutterMobile)}}}`,
  ].join('');
};
